/* eslint-disable @typescript-eslint/no-explicit-any */
import Layout from '@/components/layout/Index';
import AboutHeader from '@/components/sections/AboutHeader';
import { Faq } from '@/components/sections/Faq';
import AboutFounder from '@/components/ui/about/AboutFounder';
import WorldImpactMap, {
	ImpactMarker,
} from '@/components/ui/about/WorldImpactMap';
import { useEffect, useState } from 'react';
import { client, urlFor } from '@/sanity/client';
import { PortableText } from '@portabletext/react';
import Loader from '@/components/ui/Loader';

interface ImpactMapData {
	title?: string;
	subtitle?: string;
	locations?: {
		country: string;
		latitude: number;
		longitude: number;
	}[];
}

interface HeaderData {
	heading?: string;
	subheading?: string;
	backgroundImage?: any;
}

interface AboutPageData {
	introduction: any[];
	impactMap: ImpactMapData;
	header: HeaderData;
	// Add other fields as needed
}

const About = () => {
	const [introduction, setIntroduction] = useState([]);
	const [impactMapData, setImpactMapData] = useState<ImpactMapData>({});
	const [headerData, setHeaderData] = useState<HeaderData>({});
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchAboutPageData = async () => {
			try {
				const data: AboutPageData = await client.fetch(`
                *[_type == "aboutPage"][0] {
                  introduction,
                  impactMap {
                    title,
                    subtitle,
                    locations[] {
                      country,
                      latitude,
                      longitude
                    }
                  },
          header {
            heading,
            subheading,
            backgroundImage
          }
                }
              `);

				if (data) {
					setIntroduction(data.introduction || []);
					setImpactMapData(data.impactMap || {});
					setHeaderData(data.header || {});
				}
			} catch (error) {
				console.error('Error fetching about page data:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchAboutPageData();
	}, []);

	if (loading) {
		return <Loader />;
	}

	// Convert Sanity data format to the format expected by WorldImpactMap
	const impactMarkers: ImpactMarker[] = (impactMapData.locations || []).map(
		(location) => ({
			name: location.country,
			coordinates: [location.longitude, location.latitude], // Note: order is [longitude, latitude]
			description: `Impact in ${location.country}`,
		}),
	);

	// Get background image URL from Sanity
	const backgroundImageUrl = headerData.backgroundImage
		? urlFor(headerData.backgroundImage).url()
		: undefined;

	return (
		<div className='flex flex-col w-full overflow-hidden'>
			<Layout>
				<main className='w-full'>
					<AboutHeader backgroundImage={backgroundImageUrl} />
					<article className='text-md text-neutral-800 md:p-20 py-12 md:px-28 bg-background rounded-t-3xl relative z-20 -mt-5'>
						<div className='md:px-21 px-10'>
							<PortableText
								value={introduction}
								components={{
									block: {
										h1: ({ children }) => (
											<h1 className='text-3xl font-bold my-4'>{children}</h1>
										),
										h2: ({ children }) => (
											<h2 className='text-2xl font-bold my-3'>{children}</h2>
										),
										h3: ({ children }) => (
											<h3 className='text-xl font-bold my-2'>{children}</h3>
										),
										h4: ({ children }) => (
											<h4 className='text-lg font-semibold my-2'>{children}</h4>
										),
										normal: ({ children }) => (
											<p className='mb-4'>{children}</p>
										),
										blockquote: ({ children }) => (
											<blockquote className='pl-4 border-l-4 border-gray-300 italic my-4'>
												{children}
											</blockquote>
										),
									},
									list: {
										bullet: ({ children }) => (
											<ul className='list-disc pl-6 mb-4'>{children}</ul>
										),
										number: ({ children }) => (
											<ol className='list-decimal pl-6 mb-4'>{children}</ol>
										),
									},
									marks: {
										strong: ({ children }) => (
											<strong className='font-bold'>{children}</strong>
										),
										em: ({ children }) => (
											<em className='italic'>{children}</em>
										),
										link: ({ value, children }) => {
											const target = (value?.href || '').startsWith('http')
												? '_blank'
												: undefined;
											return (
												<a
													href={value?.href}
													target={target}
													rel={
														target === '_blank'
															? 'noopener noreferrer'
															: undefined
													}
													className='text-blue-600 hover:underline'
												>
													{children}
												</a>
											);
										},
									},
								}}
							/>
						</div>
					</article>
					<AboutFounder />
					<WorldImpactMap markers={impactMarkers} />
					<Faq />
				</main>
			</Layout>
		</div>
	);
};

export default About;
